import { ReactNode, type ReactElement } from 'react';
import type { ControlledTableProps, FetcherState, RowType, TableController, TablePageProps } from './types';
import { useQueryState } from './useQueryState';
import { TablePage } from './TablePage';
import { Navigate } from 'react-router';
import { Box, DataTable, Pagination } from '@ff-it/ui';
import { useFetcher } from './useFetcher';

const defaultPageSizes = [15, 30, 60];

export function ControlledTable<R extends RowType>(props: ControlledTableProps<R>): ReactElement {
  const {
    pageSize,
    defaultPageSize,
    defaultFilter: outerDefaultFilter,
    storageKey,
    queryParams,
    tableProps: { after, ...tableProps } = {},
    ...rest
  } = props;

  const { initialState, navigateTo, onStateChange, defaultFilter } = useFetcher({
    pageSize,
    defaultPageSize,
    queryParams,
    defaultFilter: outerDefaultFilter,
    storageKey,
  });

  if (navigateTo) {
    return <Navigate to={navigateTo} replace={true} />;
  }

  function renderTable({
    data,
    onPageChange,
    pageIndex,
    pageSize,
    columns,
    sort,
    onSort,
    isLoading,
  }: TableController<R>): ReactNode {
    return (
      <DataTable<R>
        sort={sort}
        onSort={onSort}
        data={data ? data.rows : []}
        columns={columns}
        loading={isLoading}
        after={
          <>
            {after}
            {Number.isFinite(pageSize) ? (
              <Box my="2">
                <Pagination
                  current={pageIndex}
                  pageSize={pageSize}
                  total={data?.total || 0}
                  // no pageSizeOptions if page size is constant
                  pageSizeOptions={pageSize ? undefined : defaultPageSizes}
                  onChange={onPageChange}
                  size={tableProps?.size as any}
                />
              </Box>
            ) : null}
          </>
        }
        {...tableProps}
      />
    );
  }

  return (
    <QueryTablePage
      initialState={initialState}
      onStateChange={onStateChange}
      defaultFilter={defaultFilter}
      renderTable={renderTable}
      {...rest}
    />
  );
}

const QueryTablePage = ({
  initialState,
  onStateChange,
  ...props
}: Omit<TablePageProps<any>, 'state' | 'setState'> & {
  initialState: FetcherState;
  onStateChange?: (state: FetcherState) => void;
}): ReactElement => {
  const [state, setState] = useQueryState(initialState, onStateChange);
  return <TablePage state={state} setState={setState} {...props} />;
};
